import React, { useEffect, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import FormInput from "../../components/FormInput";
import searchIcon from "../../assets/icons/search.svg";
import filterIcon from "../../assets/icons/funnel.svg";
import toggleBtn from "../../assets/icons/toggle_btn.svg";
import allUsersIcon from "../../assets/icons/all_users.svg";
import lecturerIcon from "../../assets/icons/lecturers.svg";
import studentIcon from "../../assets/icons/students.svg";
import otherUsersIcon from "../../assets/icons/other_users.svg";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import PaginationComponent from "../../components/PaginationComponent";
import TableColumnDropdownCheck from "../../components/TableColumnDropdownCheck";
import UserWalletDetails from "./UserWalletDetails";
import moment from "moment";


export interface User {
	_id: string,
	firstName: string,
	lastName: string,
	email: string,
	phoneNumber: string,
	userType: string | any,
	institution: string,
	department: string,
	disabled: boolean,
	status: string,
};

export interface WalletData {
	_id: string,
	user: User,
	walletId: string,
	balance: number,
	commission: number,
	status: string,
	disabled: boolean,
	userType: string | any,
	totalCredit: number,
	totalDebit: number,
	createdAt: string,
};

const WalletsTable = () => {

	const [wallets, setWallets] = useState<WalletData[]>([]);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>('All');
	const [isLoading, setIsLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({
		currentPage: 1,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		nextPage: null,
		previousPage: null
	});

	const handlePaginate = (pageNumber: number) => {
		setPaginationData((prevData) => ({ ...prevData, currentPage: pageNumber }));
	};

	const fetchWallets = async () => {
		setIsLoading(true);
		try {
			api.setAuthorization(api.getLoggedInUser());
			const queryParams = new URLSearchParams({ page: paginationData.currentPage?.toString() || '1' });
			if (selectedCategory && selectedCategory !== 'All') {
				queryParams.set('category', selectedCategory);
			}
			const response = await api.get(endpoints.getAllWallets, queryParams);
			const fetchedWallets: WalletData[] = response.data.data.docs;
			setWallets(fetchedWallets);
			setPaginationData({
				currentPage: response.data.data.page,
				totalPages: response.data.data.totalPages,
				hasNextPage: response.data.data.hasNextPage,
				hasPreviousPage: response.data.data.hasPreviousPage,
				nextPage: response.data.data.nextPage,
				previousPage: response.data.data.previousPage
			});
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching transactions:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchWallets();
	}, [paginationData.currentPage, selectedCategory]);

	const handleCategoryClick = (category: string) => {
		setSelectedCategory(category);
	};

	const formatAmount = (price: number | bigint) => {
		return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 }).format(price);
	};

	const formatDate = (dateString: moment.MomentInput) => {
		return moment(dateString).format("DD-MM-YYYY");
	};

	const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
	const allColumns = [
		'ID', 'Created On', 'User', 'User Type', 'Balance', 'Inflow', 'Outflow', 'Status'
	];

	const [showModal, setShowModal] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

	const handleShowWalletDetailModal = (userId: string,) => {
		setSelectedUserId(userId);
		setShowModal(true);
	};

	const handleHideWalletDetailModal = () => {
		setShowModal(false);
		setSelectedUserId(null);
	};

	const capitalizeFirstLetter = (string: string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};


	return (
		<>
			<div className="categories">
				<div className={`category ${selectedCategory === 'All' ? 'category-active' : ''}`}
					onClick={() => handleCategoryClick('All')}>
					<img src={allUsersIcon} alt="all" />
					<span>
						All Users
					</span>
				</div>
				<div className={`category ${selectedCategory === 'lecturer' ? 'category-active' : ''}`}
					onClick={() => handleCategoryClick('lecturer')}>
					<img src={lecturerIcon} alt="lecturer" />
					<span>
						Lecturers
					</span>
				</div>
				<div className={`category ${selectedCategory === 'student' ? 'category-active' : ''}`}
					onClick={() => handleCategoryClick('student')}>
					<img src={studentIcon} alt="student" />
					<span>
						Students
					</span>
				</div>
				<div className={`category ${selectedCategory === 'regular' ? 'category-active' : ''}`}
					onClick={() => handleCategoryClick('regular')}>
					<img src={otherUsersIcon} alt="others" />
					<span>
						Other Users
					</span>
				</div>
			</div>
			<div className="table-topbar">
				<div className="search_bar">
					<img src={searchIcon} alt="search" />
					<FormInput name="search" placeholder="Search" />
				</div>
				<div className="action-flex">
					<div className="column-select">
						<TableColumnDropdownCheck
							options={allColumns}
							selectedOptions={selectedColumns}
							onSelectionChange={setSelectedColumns}
						/>
					</div>
				</div>
			</div>
			<div className="table-body">
				<Table>
					{isLoading ? (
						<div className="spinner-div">
							<Spinner animation="border" size="sm" role="status" />
						</div>
					) : wallets.length > 0 ? (
						<>
							<thead>
								<tr>
									<th className="custom-thead-bg">
										<FormInput name="requery" type="checkbox" />
									</th>
									{selectedColumns.includes('ID') && <th className="custom-thead-bg">ID</th>}
									{selectedColumns.includes('Created On') && <th className="custom-thead-bg">Created On</th>}
									{selectedColumns.includes('User') && <th className="custom-thead-bg">User</th>}
									{selectedColumns.includes('User Type') && <th className="custom-thead-bg">User Type</th>}
									{selectedColumns.includes('Balance') && <th className="custom-thead-bg">Balance</th>}
									{selectedColumns.includes('Inflow') && <th className="custom-thead-bg">Inflow</th>}
									{selectedColumns.includes('Outflow') && <th className="custom-thead-bg">Outflow</th>}
									{selectedColumns.includes('Status') && <th className="custom-thead-bg">Status</th>}
									<th className="custom-thead-bg"></th>
								</tr>
							</thead>
							<tbody>
								{wallets.map((wallet, index) => (
									<tr key={wallet._id}>
										<td>
											<div>
												<FormInput name="requery" type="checkbox" />
											</div>
										</td>
										{selectedColumns.includes('ID') && <td>{wallet.walletId}</td>}
										{selectedColumns.includes('Created On') && <td>{formatDate(wallet.createdAt)}</td>}
										{selectedColumns.includes('User') &&
											<td>
												{`${wallet.user?.firstName} ${wallet.user?.lastName}`} <br />
												{wallet.user?.email}
											</td>
										}
										{selectedColumns.includes('User Type') && <td>{capitalizeFirstLetter(wallet.user?.userType)}</td>}
										{selectedColumns.includes('Balance') && <td className="wallet-balance">{formatAmount(wallet.balance)}</td>}
										{selectedColumns.includes('Inflow') && <td className="wallet-inflow">{formatAmount(wallet.totalCredit)}</td>}
										{selectedColumns.includes('Outflow') && <td className="wallet-outflow">{formatAmount(wallet.totalDebit)}</td>}
										{selectedColumns.includes('Status') &&
											<td>
												<div className={wallet.status ? "book-status-enabled" : "book-status-disabled"}>
													{wallet.status ? 'ENABLED' : 'DISABLED'}
												</div>
											</td>
										}
										<td>
											<div className="custom-popover">
												<img src={toggleBtn} alt="toggle open details" onClick={() => handleShowWalletDetailModal(wallet.user?._id)} style={{ cursor: 'pointer' }} />
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</>
					) : (
						<div className="no-results">
							No wallets found.
						</div>
					)}
				</Table>
			</div>

			<PaginationComponent
				currentPage={paginationData.currentPage}
				totalPages={paginationData.totalPages}
				hasNextPage={paginationData.hasNextPage}
				hasPreviousPage={paginationData.hasPreviousPage}
				nextPage={paginationData.nextPage}
				previousPage={paginationData.previousPage}
				onPaginate={handlePaginate}
			/>

			{selectedUserId && (
				<UserWalletDetails
					showModal={showModal}
					onHide={handleHideWalletDetailModal}
					userId={selectedUserId}
					formatAmount={formatAmount}
					capitalizeFirstLetter={capitalizeFirstLetter}
				/>
			)}
		</>
	)
}
export default WalletsTable